import { Product } from 'model'
import { GetStaticProps, InferGetStaticPropsType, GetStaticPaths } from 'next'
import { Page, ProductDetailWrapper } from 'core/components'
import { useRouter } from 'next/router'
import { fetchStaticPropsData } from 'helpers/sanity'
import { ProductSeo, ShopLayout } from 'components'
import { ReactElement } from 'react'
import { ProductDetail, ProductFeatures, ProductPhotos, RelatedProducts } from 'components/Shop'
import { getSanityClient, PRODUCT_FIELDS } from '@resellam/sanity'
import { Stack } from 'ui/core'
import { fetch, filter, slice } from '@aonawale/sanity-query'

export const getStaticPaths: GetStaticPaths = async () => {
  const products = await fetch<Product[]>(
    getSanityClient(),
    {
      constraints: [filter('_type', '==', 'product'), filter('featured', '==', true)],
    },
    PRODUCT_FIELDS,
  )
  const paths = products.map((product) => ({
    params: { product: product.slug },
  }))
  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<{ product: Product }> = (context) =>
  fetchStaticPropsData(
    'product',
    {
      constraints: [filter('slug.current', '==', context.params?.product), slice(0)],
    },
    PRODUCT_FIELDS,
  )

const ProductPage = ({ product }: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter()
  const loading = router.isFallback
  const goBack = () => (product ? router.replace('/shop') : undefined)

  return (
    <Page
      key={product?.id}
      loading={loading}
      title={product?.title}
      onClickBack={goBack}
    >
      {product && (
        <ProductSeo
          product={product}
          path={`/shop/${product.slug}`}
          title={`Buy ${product.condition} ${product.title}`}
        />
      )}
      <Stack gap={40}>
        <ProductDetailWrapper loading={loading} product={product}>
          {product && <ProductDetail product={product} />}
        </ProductDetailWrapper>
        {product?.metas?.length ? <ProductFeatures product={product} /> : null}
        {product?.images?.length ? <ProductPhotos photos={product.images || []} /> : null}
        {product && <RelatedProducts product={product} />}
      </Stack>
    </Page>
  )
}

ProductPage.getLayout = (page: ReactElement) => <ShopLayout>{page}</ShopLayout>

export default ProductPage
